// Straight from `./contrast`, not the barrel: the barrel re-exports this file,
// so going through it would be a cycle.
import { parseColor } from "./contrast";

/** What the browser paints when nothing in the tree provides a background. */
const DEFAULT_CANVAS = "rgb(255, 255, 255)";

/**
 * The background colour the text of `element` actually sits on.
 *
 * Walks up from `element` until it finds an ancestor that paints something,
 * falling back to the default canvas at the root. Computed backgrounds always
 * serialize as `rgb()` / `rgba()`, so `parseColor` can read them.
 *
 * Simplification worth naming: the first non-transparent ancestor wins.
 * Stacked translucent layers are not blended, since the resulting ratio would
 * be approximate either way and the rule only needs to catch text that
 * vanishes.
 */
export function getEffectiveBackgroundColor(element: Element): string {
	let current: Element | null = element;

	while (current) {
		const background = getComputedStyle(current).backgroundColor;
		const parsed = parseColor(background);
		if (parsed && parsed.a > 0) return background;
		current = current.parentElement;
	}

	return DEFAULT_CANVAS;
}

/**
 * What shows through the background of `element` itself: the effective
 * background of its parent. A translucent `backgroundColor` an app paints on
 * `element` is flattened onto this before its contrast is judged.
 */
export function getUnderlyingBackgroundColor(element: Element): string {
	return element.parentElement
		? getEffectiveBackgroundColor(element.parentElement)
		: DEFAULT_CANVAS;
}

/**
 * `read(element)` as it would be without the inline `property` of
 * `element`, which is what `""` hands back to the theme for that option.
 *
 * The inline declaration is lifted and put back synchronously, inside the
 * same task, so the page never renders the in-between state — with its
 * priority, so an `!important` declaration comes back as `!important`.
 */
function withoutInline(
	element: Element,
	property: string,
	read: (element: Element) => string,
): string {
	if (!(element instanceof HTMLElement)) return read(element);
	const inline = element.style.getPropertyValue(property);
	if (!inline) return read(element);

	const priority = element.style.getPropertyPriority(property);
	element.style.removeProperty(property);
	try {
		return read(element);
	} finally {
		element.style.setProperty(property, inline, priority);
	}
}

/**
 * The background the text of `element` sits on once its *inline* background
 * is cleared, which is what `backgroundColor: ""` hands back to the theme.
 */
export function getThemeBackgroundColor(element: Element): string {
	return withoutInline(
		element,
		"background-color",
		getEffectiveBackgroundColor,
	);
}

/**
 * The colour of the text of `element` once its *inline* colour is cleared,
 * which is what `fontColor: ""` hands back to the theme.
 */
export function getThemeTextColor(element: Element): string {
	return withoutInline(element, "color", (el) => getComputedStyle(el).color);
}

/**
 * Whether `element` carries an inline `property` — for the colours, one an
 * app wrote, since the themes style these elements from their stylesheets.
 */
export function hasInlineStyle(element: Element, property: string): boolean {
	return (
		element instanceof HTMLElement &&
		element.style.getPropertyValue(property) !== ""
	);
}
